<template>
    <AdminLayout>
        <template #header>
            <Header :menu="main_menu" v-if="$page.props.auth.user" />
        </template>
        <template #default>
            <div class="space-y-6">
                <!-- Header -->
                <div class="flex items-center justify-between">
                    <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>
                    <button @click="refreshData"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-gray-700 uppercase transition duration-150 ease-in-out bg-gray-200 border border-gray-300 rounded-md hover:bg-gray-300">
                        <i class="mr-2 fas fa-sync-alt"></i>
                        Refresh
                    </button>
                </div>

                <!-- Statistics Cards -->
                <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <div class="overflow-hidden bg-white border-l-4 border-blue-500 rounded-lg shadow-lg">
                        <div class="p-6">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <i class="text-2xl text-blue-500 fas fa-user-check"></i>
                                </div>
                                <div class="flex-1 w-0 ml-5">
                                    <dl>
                                        <dt class="text-sm font-medium text-gray-500 truncate">
                                            Kehadiran Hari Ini
                                        </dt>
                                        <dd class="text-lg font-medium text-gray-900">
                                            {{ stats.total_kehadiran_hari_ini || 0 }}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="overflow-hidden bg-white border-l-4 border-green-500 rounded-lg shadow-lg">
                        <div class="p-6">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <i class="text-2xl text-green-500 fas fa-calendar-alt"></i>
                                </div>
                                <div class="flex-1 w-0 ml-5">
                                    <dl>
                                        <dt class="text-sm font-medium text-gray-500 truncate">
                                            Agenda Hari Ini
                                        </dt>
                                        <dd class="text-lg font-medium text-gray-900">
                                            {{ stats.total_agenda_hari_ini || 0 }}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="overflow-hidden bg-white border-l-4 border-yellow-500 rounded-lg shadow-lg">
                        <div class="p-6">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <i class="text-2xl text-yellow-500 fas fa-bullhorn"></i>
                                </div>
                                <div class="flex-1 w-0 ml-5">
                                    <dl>
                                        <dt class="text-sm font-medium text-gray-500 truncate">
                                            Pengumuman Aktif
                                        </dt>
                                        <dd class="text-lg font-medium text-gray-900">
                                            {{ stats.total_pengumuman_aktif || 0 }}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="overflow-hidden bg-white border-l-4 border-red-500 rounded-lg shadow-lg">
                        <div class="p-6">
                            <div class="flex items-center">
                                <div class="flex-shrink-0">
                                    <i class="text-2xl text-red-500 fas fa-envelope"></i>
                                </div>
                                <div class="flex-1 w-0 ml-5">
                                    <dl>
                                        <dt class="text-sm font-medium text-gray-500 truncate">
                                            Undangan Hari Ini
                                        </dt>
                                        <dd class="text-lg font-medium text-gray-900">
                                            {{ stats.total_undangan_hari_ini || 0 }}
                                        </dd>
                                    </dl>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <!-- Kehadiran Hari Ini -->
                    <div class="bg-white rounded-lg shadow-lg">
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3 class="text-lg font-medium text-gray-900">Kehadiran Pimpinan Hari Ini</h3>
                            <Link :href="route('kehadiran.index')" class="text-sm text-blue-600 hover:text-blue-900">
                            Lihat Semua
                            </Link>
                        </div>
                        <div class="p-6">
                            <div v-if="kehadiran_hari_ini.length === 0" class="py-8 text-center text-gray-500">
                                <i class="mb-4 text-4xl fas fa-inbox"></i>
                                <p>Belum ada data kehadiran hari ini.</p>
                            </div>
                            <div v-else class="overflow-x-auto">
                                <table class="min-w-full divide-y divide-gray-200">
                                    <thead class="bg-gray-50">
                                        <tr>
                                            <th
                                                class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Nama
                                            </th>
                                            <th
                                                class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Jabatan
                                            </th>
                                            <th
                                                class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Status
                                            </th>
                                            <th
                                                class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Jam Masuk
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="bg-white divide-y divide-gray-200">
                                        <tr v-for="kehadiran in kehadiran_hari_ini.slice(0, 5)" :key="kehadiran.id">
                                            <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                {{ kehadiran.nama_pimpinan }}
                                            </td>
                                            <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                {{ kehadiran.jabatan }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap">
                                                <span :class="getStatusBadgeClass(kehadiran.status)"
                                                    class="inline-flex px-2 py-1 text-xs font-semibold rounded-full">
                                                    {{ getStatusLabel(kehadiran.status) }}
                                                </span>
                                            </td>
                                            <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                                {{ kehadiran.jam_masuk || '-' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <!-- Agenda Mendatang -->
                    <div class="bg-white rounded-lg shadow-lg">
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3 class="text-lg font-medium text-gray-900">Agenda Mendatang</h3>
                            <Link :href="route('agenda.index')" class="text-sm text-green-600 hover:text-green-900">
                            Lihat Semua
                            </Link>
                        </div>
                        <div class="p-6">
                            <div v-if="agenda_mendatang.length === 0" class="py-8 text-center text-gray-500">
                                <i class="mb-4 text-4xl fas fa-calendar-times"></i>
                                <p>Tidak ada agenda mendatang.</p>
                            </div>
                            <div v-else class="space-y-4">
                                <div v-for="agenda in agenda_mendatang.slice(0, 5)" :key="agenda.id"
                                    class="flex items-start space-x-3">
                                    <div class="flex-shrink-0">
                                        <div class="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
                                            <i class="text-sm text-white fas fa-calendar-alt"></i>
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <p class="text-sm font-medium text-gray-900">
                                            {{ agenda.judul }}
                                        </p>
                                        <p class="text-sm text-gray-500">
                                            {{ formatDate(agenda.tanggal) }} - {{ agenda.waktu_mulai }}
                                        </p>
                                        <p class="text-xs text-gray-400">
                                            <i class="mr-1 fas fa-map-marker-alt"></i>{{ agenda.tempat }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <!-- Pengumuman Aktif -->
                    <div class="bg-white rounded-lg shadow-lg">
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3 class="text-lg font-medium text-gray-900">Pengumuman Aktif</h3>
                            <Link :href="route('pengumuman.index')"
                                class="text-sm text-yellow-600 hover:text-yellow-900">
                            Lihat Semua
                            </Link>
                        </div>
                        <div class="p-6">
                            <div v-if="pengumuman_aktif.length === 0" class="py-8 text-center text-gray-500">
                                <i class="mb-4 text-4xl fas fa-bullhorn"></i>
                                <p>Tidak ada pengumuman aktif.</p>
                            </div>
                            <div v-else class="space-y-4">
                                <div v-for="pengumuman in pengumuman_aktif.slice(0, 5)" :key="pengumuman.id"
                                    class="flex items-start space-x-3">
                                    <div class="flex-shrink-0">
                                        <div :class="getPriorityIconClass(pengumuman.prioritas)"
                                            class="flex items-center justify-center w-8 h-8 rounded-full">
                                            <i class="text-sm text-white fas fa-bullhorn"></i>
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <p class="text-sm font-medium text-gray-900">
                                            {{ pengumuman.judul }}
                                        </p>
                                        <p class="text-sm text-gray-500">
                                            {{ truncateText(pengumuman.konten, 100) }}
                                        </p>
                                        <p class="text-xs text-gray-400">
                                            {{ formatDate(pengumuman.tanggal_publikasi) }} - {{
                                                getPriorityLabel(pengumuman.prioritas) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Undangan Mendatang -->
                    <div class="bg-white rounded-lg shadow-lg">
                        <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <h3 class="text-lg font-medium text-gray-900">Undangan Mendatang</h3>
                            <Link :href="route('undangan.index')" class="text-sm text-red-600 hover:text-red-900">
                            Lihat Semua
                            </Link>
                        </div>
                        <div class="p-6">
                            <div v-if="undangan_mendatang.length === 0" class="py-8 text-center text-gray-500">
                                <i class="mb-4 text-4xl fas fa-envelope-open"></i>
                                <p>Tidak ada undangan mendatang.</p>
                            </div>
                            <div v-else class="space-y-4">
                                <div v-for="undangan in undangan_mendatang.slice(0, 5)" :key="undangan.id"
                                    class="flex items-start space-x-3">
                                    <div class="flex-shrink-0">
                                        <div class="flex items-center justify-center w-8 h-8 bg-red-500 rounded-full">
                                            <i class="text-sm text-white fas fa-envelope"></i>
                                        </div>
                                    </div>
                                    <div class="flex-1 min-w-0">
                                        <p class="text-sm font-medium text-gray-900">
                                            {{ undangan.judul_rapat }}
                                        </p>
                                        <p class="text-sm text-gray-500">
                                            {{ formatDate(undangan.tanggal_rapat) }} - {{ undangan.waktu_mulai }}
                                        </p>
                                        <p class="text-xs text-gray-400">
                                            <i class="mr-1 fas fa-map-marker-alt"></i>{{ undangan.tempat }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </template>
        <template #footer></template>
    </AdminLayout>
</template>

<script setup>
import { defineProps, ref, onMounted } from "vue";

import AdminLayout from '@/Layouts/Container.vue'
import Header from "@/Components/Header.vue";

const props = defineProps({
    kehadiran_hari_ini: Array,
    agenda_hari_ini: Array,
    agenda_mendatang: Array,
    pengumuman_aktif: Array,
    pengumuman_urgent: Array,
    undangan_hari_ini: Array,
    undangan_mendatang: Array,
    stats: Object,
    userCount: {
        type: Object,
        required: true,
    },
    newsCount: {
        type: Object,
        required: true,
    },
    galleryCount: {
        type: Object,
        required: true,
    },
    dosenCount: {
        type: Object,
        required: true,
    },
    agendaCount: {
        type: Object,
        required: true,
    },
    agenda: {
        type: Object,
        required: true,
    },
    main_menu: {
        type: Array,
        required: true,
    },
})

const refreshData = () => {
    router.reload()
}

const getStatusBadgeClass = (status) => {
    const classes = {
        'hadir': 'bg-green-100 text-green-800',
        'tidak_hadir': 'bg-red-100 text-red-800',
        'terlambat': 'bg-yellow-100 text-yellow-800',
        'sakit': 'bg-blue-100 text-blue-800',
        'izin': 'bg-blue-100 text-blue-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusLabel = (status) => {
    const labels = {
        'hadir': 'Hadir',
        'tidak_hadir': 'Tidak Hadir',
        'terlambat': 'Terlambat',
        'sakit': 'Sakit',
        'izin': 'Izin'
    }
    return labels[status] || status
}

const getPriorityIconClass = (prioritas) => {
    const classes = {
        'urgent': 'bg-red-500',
        'tinggi': 'bg-orange-500',
        'sedang': 'bg-yellow-500',
        'rendah': 'bg-green-500'
    }
    return classes[prioritas] || 'bg-gray-500'
}

const getPriorityLabel = (prioritas) => {
    const labels = {
        'urgent': 'Urgent',
        'tinggi': 'Tinggi',
        'sedang': 'Sedang',
        'rendah': 'Rendah'
    }
    return labels[prioritas] || prioritas
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const truncateText = (text, limit) => {
    if (!text) return ''
    return text.length > limit ? text.substring(0, limit) + '...' : text
}

// Auto refresh every 5 minutes
onMounted(() => {
    setInterval(() => {
        router.reload({ only: ['kehadiran_hari_ini', 'agenda_hari_ini', 'pengumuman_aktif', 'undangan_hari_ini', 'stats'] })
    }, 300000)
})
</script>